import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react'
import { Navigation } from 'navigation'

interface ExpansionContextValue {
    expandedPath: string[]
    setExpandedAtLevel: (level: number, id: string | null) => void
    isExpandedAtLevel: (level: number, id: string) => boolean
    getExpandedIdAtLevel: (level: number) => string | null
    clearExpansion: () => void
}

const ExpansionContext = createContext<ExpansionContextValue | null>(null)

interface ExpansionProviderProps {
    navigation: Navigation
    initialPath?: string[]
    children: React.ReactNode
}

/**
 * Centralized expansion state management with URL synchronization.
 *
 * This replaces the distributed state approach where each NestedContainer
 * independently managed its own expandedElement and tried to coordinate
 * via URL updates.
 *
 * Benefits:
 * - Single source of truth for expansion state
 * - No race conditions between nested components
 * - Clean separation between state management and persistence
 * - URL updates happen only at the top level
 */
export function ExpansionProvider({ navigation, initialPath = [], children }: ExpansionProviderProps) {
    const [expandedPath, setExpandedPath] = useState<string[]>(initialPath)
    const navigationRef = useRef(navigation)

    // Keep navigation ref updated
    useEffect(() => {
        navigationRef.current = navigation
    }, [navigation])

    // Sync state to URL whenever it changes
    useEffect(() => {
        if (expandedPath.length > 0) {
            navigationRef.current.addQueryWithReplace({ listIds: expandedPath.join(',') })
        } else {
            navigationRef.current.addQueryWithReplace({ listIds: null })
        }
    }, [expandedPath])

    const setExpandedAtLevel = useCallback((level: number, id: string | null) => {
        setExpandedPath((current) => {
            const currentId = current[level]

            if (id === null || currentId === id) {
                // Collapsing: remove this level and all deeper levels
                return current.slice(0, level)
            } else {
                // Expanding: keep parent levels, set this level
                return [...current.slice(0, level), id]
            }
        })
    }, [])

    const isExpandedAtLevel = useCallback(
        (level: number, id: string) => {
            return expandedPath[level] === id
        },
        [expandedPath]
    )

    const getExpandedIdAtLevel = useCallback(
        (level: number) => {
            return expandedPath[level] || null
        },
        [expandedPath]
    )

    const clearExpansion = useCallback(() => {
        setExpandedPath([])
    }, [])

    const value: ExpansionContextValue = {
        expandedPath,
        setExpandedAtLevel,
        isExpandedAtLevel,
        getExpandedIdAtLevel,
        clearExpansion,
    }

    return <ExpansionContext.Provider value={value}>{children}</ExpansionContext.Provider>
}

export function useExpansion() {
    const context = useContext(ExpansionContext)
    if (!context) {
        throw new Error('useExpansion must be used within ExpansionProvider')
    }
    return context
}
